import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, X } from 'lucide-react'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Select,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui'
import { UserAvatar } from '@/components/UserProfileCard'
import { useUserList } from '@/features/user'
import { ERoleType, useGetRoles } from '@/features/user-management'
import { useDebounce } from '@/hooks/useDebounce'
import { EScopeLevel, User } from '@/schema'
import { formatFullName, validateEmail } from '@/utils'
import {
  DEFAULT_GET_ALL_LIMIT,
  SEARCH_DEBOUNCE_DELAY_MS,
} from '@/utils/constants'

import { useDeliverableSetsStore } from '../store'
import { IInviteUserDeliverableSet } from '../types'
import { isAssignedInProject } from '../utils'

interface UserWithFullName extends User {
  fullName: string
}

const InviteReviewerField: React.FC = () => {
  const {
    showSendApprovalDialog,
    requestApprovalDeliverableSet,
    inviteUsers,
    setInviteUserDialogOpen,
    setInviteUsers,
    setInviteUserSearchText,
    updateInviteUserRole,
  } = useDeliverableSetsStore()

  const { t } = useTranslation()

  const { roles, availableRoles, reviewerRole } = useGetRoles({
    enabled: showSendApprovalDialog,
    scopeLevel: EScopeLevel.DELIVERABLE_SET,
  })

  const [searchText, setSearchText] = useState<string>('')
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const containerRef = React.useRef<HTMLDivElement>(null)

  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_DELAY_MS)
  const trimmedSearch = React.useMemo(
    () => debouncedSearchText.trim(),
    [debouncedSearchText]
  )

  const {
    data: { data = [] } = {},
    isLoading,
    isPending,
    isFetching,
  } = useUserList({
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
    search: trimmedSearch,
    enabled: showSendApprovalDialog,
  })

  React.useEffect(() => {
    if (!inviteUsers.length) return

    const isHaveReviewer = inviteUsers.find(
      (u) => u.roleName === ERoleType.REVIEWER
    )

    if (isHaveReviewer) setErrorMsg(null)
    else setErrorMsg('Reviewer is required')
  }, [inviteUsers, requestApprovalDeliverableSet])

  const users: UserWithFullName[] = React.useMemo(() => {
    // Map users to include full name
    const usersWithFullName = data.map((u) => ({
      ...u,
      fullName: formatFullName(u.first_name, u.last_name),
    }))

    return usersWithFullName.filter((u) => {
      const isAlreadyInvited = inviteUsers.some((iu) => iu.id === u.id)
      const isOwner = u.id === requestApprovalDeliverableSet?.owner_id
      const isOrgAdmin = u.roles?.some(
        (r) => r.role_name === ERoleType.ORGANIZATION_ADMIN
      )
      const isAssignedForProject = u.roles?.some((r) =>
        isAssignedInProject(r, requestApprovalDeliverableSet?.project_id)
      )

      return (
        !isAlreadyInvited && !isOwner && !isOrgAdmin && !isAssignedForProject
      )
    })
  }, [
    data,
    inviteUsers,
    requestApprovalDeliverableSet?.owner_id,
    requestApprovalDeliverableSet?.project_id,
  ])

  // Check if the user is a new user
  const isAddNewUser = React.useMemo(() => {
    // Check if the search text is a valid email
    if (!data.length || !trimmedSearch.length || validateEmail(trimmedSearch)) {
      return false
    }
    return !data.some((u) => u.email === trimmedSearch)
  }, [data, trimmedSearch])

  const isDisabledStandalone = React.useMemo(
    () => (isPending || isFetching || isLoading) && !searchText.length,
    [isFetching, isLoading, isPending, searchText.length]
  )

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputValueChange = React.useCallback((search: string) => {
    setSearchText(search)
    setIsOpen(true)
  }, [])

  const handleClear = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setSearchText('')
    setIsOpen(false)
  }, [])

  const handleBlur = React.useCallback(() => {
    setSearchText('')
  }, [])

  const handleSelectUser = React.useCallback(
    (user: UserWithFullName) => {
      setInviteUsers([
        ...inviteUsers,
        {
          id: user.id,
          firstName: user.first_name,
          lastName: user.last_name,
          email: user.email,
          roleId: reviewerRole?.id || '',
          roleName: reviewerRole?.role_name || '',
        },
      ])

      // Clear the search text and close the dropdown
      setSearchText('')
      setIsOpen(false)
    },
    [inviteUsers, reviewerRole?.id, reviewerRole?.role_name, setInviteUsers]
  )

  const handleAddNewUser = React.useCallback(() => {
    setInviteUserSearchText(trimmedSearch)
    setInviteUserDialogOpen(true)
  }, [setInviteUserDialogOpen, setInviteUserSearchText, trimmedSearch])

  const handleRemoveUser = React.useCallback(
    (user: IInviteUserDeliverableSet) => {
      setInviteUsers(
        inviteUsers.filter((u) => {
          if (u.id) return u.id !== user.id
          if (u.uuid) return u.uuid !== user.uuid
          return true
        })
      )
    },
    [inviteUsers, setInviteUsers]
  )

  const renderCommandContent = React.useCallback(() => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-8">
          <Spinner />
        </div>
      )
    }

    // Render add new user button if there are no users or the user is a new user
    if (data.length === 0 || isAddNewUser) {
      return (
        <CommandEmpty className="p-1">
          <button
            type="button"
            className="py-1.5 px-2 flex items-center gap-4 text-sm cursor-pointer w-full bg-transparent text-left"
            onClick={handleAddNewUser}
          >
            <div className="size-7 flex items-center justify-center rounded-full bg-brand-100 text-gray-700 font-semibold text-xs">
              <Plus size={16} className="text-brand-600" />
            </div>
            <div className="flex flex-col">
              <div className="text-sm font-medium leading-5 text-brand-600">
                Add new user
              </div>
              <div className="text-xs leading-4 text-gray-500">
                {`Add "${trimmedSearch}" as new user`}
              </div>
            </div>
          </button>
        </CommandEmpty>
      )
    }

    if (users.length === 0) {
      return (
        <CommandEmpty>
          <div className="text-sm text-gray-500">No users found</div>
        </CommandEmpty>
      )
    }

    return (
      <>
        {users.length > 0 ? (
          <CommandGroup>
            {users.map((user) => (
              <CommandItem
                key={user.id}
                className="flex items-center gap-4 text-sm cursor-pointer"
                value={user.id}
                onSelect={() => handleSelectUser(user)}
              >
                <UserAvatar
                  avatarUrl={user.avatar_url || ''}
                  userName={user.fullName}
                  className="size-7"
                  textClassName="text-xs"
                />
                <div className="flex flex-col">
                  <div className="text-sm font-medium leading-5">
                    {user.fullName}
                  </div>
                  <div className="text-xs leading-4 text-gray-500">
                    {user.email}
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
      </>
    )
  }, [
    data.length,
    handleAddNewUser,
    handleSelectUser,
    isAddNewUser,
    isLoading,
    trimmedSearch,
    users,
  ])

  return (
    <>
      <div className="space-y-1">
        <label
          htmlFor="reviewers"
          className="text-sm font-medium flex items-center justify-between"
        >
          <div className="flex items-center gap-1.5">
            Reviewer / Viewer
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <div className="size-4 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 cursor-help">
                  <span className="text-[10px] font-bold px-[1px] leading-tight">
                    i
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('deliverableSet.dialog.info.reviewerTooltip')}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </label>
        <div ref={containerRef} className="relative">
          <Command className="overflow-visible" shouldFilter={false}>
            <CommandInput
              value={searchText || ''}
              onValueChange={handleInputValueChange}
              disabled={isDisabledStandalone}
              clearable={!!searchText}
              onClear={handleClear}
              onBlur={handleBlur}
              placeholder="Enter name or email"
              onFocus={() => {
                setIsOpen(true)
              }}
            />
            {isOpen && (
              <CommandList className="absolute z-50 top-10 w-full mt-1 max-h-48 overflow-auto rounded-md border border-input bg-white shadow-lg">
                {renderCommandContent()}
              </CommandList>
            )}
          </Command>
        </div>
      </div>
      <div className="space-y-4 max-h-56 overflow-auto empty:hidden">
        {inviteUsers.map((user, index) => {
          const fullName = formatFullName(user.firstName, user.lastName)

          return (
            <div
              key={user.id || user.uuid}
              className="py-1.5 pl-6 pr-4 flex items-center gap-4 text-sm cursor-pointer w-full bg-transparent text-left rounded-lg border border-solid border-gray-light-300"
            >
              <UserAvatar
                avatarUrl=""
                userName={fullName}
                className="size-9"
                textClassName="text-xs"
              />
              <div className="flex flex-col">
                <div className="text-sm font-medium leading-5">{fullName}</div>
                <div className="text-xs leading-4 text-gray-500">
                  {user.email}
                </div>
              </div>
              <div className="ml-auto flex gap-2 items-center justify-end">
                {user.uuid ? (
                  <div className="px-2 py-1 text-xs leading-4 rounded-sm bg-gray-50 border border-solid border-gray-light-300 text-warning-400">
                    Invite
                  </div>
                ) : null}

                <Select
                  value={user.roleId}
                  onChange={(val) => {
                    if (!val) return

                    updateInviteUserRole({
                      id: user.id,
                      uuid: user.uuid,
                      roleId: val,
                      roleName:
                        (roles.find((r) => r.id === val)
                          ?.role_name as string) || '',
                      index,
                    })
                  }}
                  placeholder="Select role"
                  options={availableRoles}
                  disabled={!availableRoles.length}
                />
              </div>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 p-0 bg-transparent border-none cursor-pointer text-foreground-fg-brand-secondary-alt"
                    aria-label="Not review"
                    onClick={() => handleRemoveUser(user)}
                  >
                    <X
                      size={16}
                      className="size-4 text-foreground-fg-quaternary-400"
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <span>Remove</span>
                </TooltipContent>
              </Tooltip>
            </div>
          )
        })}
      </div>
      {errorMsg && <p className="mt-1 text-sm text-red-500">{errorMsg}</p>}
    </>
  )
}

const InviteReviewerFieldMemoized = React.memo(InviteReviewerField)
InviteReviewerFieldMemoized.displayName = 'InviteReviewerField'

export { InviteReviewerFieldMemoized as InviteReviewerField }
